import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { 
  MessageCircle, 
  Send, 
  User, 
  GraduationCap,
  ArrowLeft,
  Search,
  Clock,
  Check,
  CheckCheck,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function ParentMessages() {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [initialMessage, setInitialMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchThreads();
  }, []);

  useEffect(() => {
    if (selectedThread) {
      fetchMessages(selectedThread.id);
    }
  }, [selectedThread]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchThreads = async () => {
    try {
      const response = await fetch(`${API}/parent/messages/threads`, {
        credentials: "include"
      });
      const data = await response.json();
      if (data.success) {
        setThreads(data.threads || []);
      }
    } catch (error) {
      toast.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (threadId) => {
    try {
      const response = await fetch(`${API}/parent/messages/threads/${threadId}`, {
        credentials: "include"
      });
      const data = await response.json();
      if (data.success) {
        setMessages(data.messages || []);
        // Update thread unread count locally
        setThreads(prev => prev.map(t => 
          t.id === threadId ? { ...t, parent_unread_count: 0 } : t
        ));
      }
    } catch (error) {
      toast.error("Failed to load messages");
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await fetch(`${API}/parent/messages/teachers`, {
        credentials: "include"
      });
      const data = await response.json();
      if (data.success) {
        setTeachers(data.teachers || []);
      }
    } catch (error) {
      toast.error("Failed to load teachers");
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThread) return;

    setSendingMessage(true);
    try {
      const response = await fetch(`${API}/parent/messages/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          thread_id: selectedThread.id,
          content: newMessage.trim()
        })
      });

      const data = await response.json();
      if (data.success) {
        setMessages(prev => [...prev, data.message]);
        setNewMessage("");
        // Update thread's last message
        setThreads(prev => prev.map(t => 
          t.id === selectedThread.id 
            ? { ...t, last_message: data.message.content, last_message_at: data.message.sent_at }
            : t
        ));
      } else {
        toast.error(data.error || "Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleStartNewChat = async () => {
    if (!selectedTeacher || !selectedStudent || !initialMessage.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await fetch(`${API}/parent/messages/threads/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          teacher_id: selectedTeacher.teacher_id,
          student_id: selectedStudent.id,
          initial_message: initialMessage.trim()
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success("Conversation started!");
        setShowNewChatModal(false);
        setSelectedTeacher(null);
        setSelectedStudent(null);
        setInitialMessage("");
        fetchThreads();
        // Select the new thread
        if (data.thread) {
          setSelectedThread(data.thread);
        }
      } else {
        toast.error(data.error || "Failed to start conversation");
      }
    } catch (error) {
      toast.error("Failed to start conversation");
    }
  };

  const openNewChatModal = () => {
    fetchTeachers();
    setShowNewChatModal(true);
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const filteredThreads = threads.filter(t => 
    t.teacher_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.student_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUnread = threads.reduce((sum, t) => sum + (t.parent_unread_count || 0), 0);

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            onClick={() => navigate("/parent/dashboard")}
            className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
            data-testid="back-to-profile-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Profile
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-indigo-600" />
              Messages
              {totalUnread > 0 && (
                <Badge className="bg-red-500 text-white ml-2">{totalUnread}</Badge>
              )}
            </h1>
            <p className="text-slate-500 text-sm">Chat with your child's teachers</p>
          </div>
        </div>
        <Button onClick={openNewChatModal} className="bg-indigo-600 hover:bg-indigo-700">
          <MessageCircle className="w-4 h-4 mr-2" />
          New Message
        </Button>
      </div>

      <div className="flex-1 flex gap-4 min-h-0">
        {/* Thread List */}
        <Card className="w-80 flex flex-col">
          <CardHeader className="py-3 px-4 border-b">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input 
                placeholder="Search conversations..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </CardHeader>
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="p-4 text-center text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : filteredThreads.length === 0 ? (
              <div className="p-4 text-center text-slate-500">
                <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No conversations yet</p>
                <Button variant="link" onClick={openNewChatModal} className="text-indigo-600">
                  Start a new chat
                </Button>
              </div>
            ) : (
              filteredThreads.map((thread) => (
                <div
                  key={thread.id}
                  onClick={() => setSelectedThread(thread)}
                  className={`p-3 border-b cursor-pointer hover:bg-slate-50 transition-colors ${
                    selectedThread?.id === thread.id ? 'bg-indigo-50 border-l-4 border-l-indigo-600' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="w-10 h-10 bg-indigo-100">
                      <AvatarFallback className="bg-indigo-100 text-indigo-600 text-sm">
                        {getInitials(thread.teacher_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-800 truncate">
                          {thread.teacher_name}
                        </span>
                        <span className="text-xs text-slate-400">
                          {formatTime(thread.last_message_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <GraduationCap className="w-3 h-3" />
                        <span>{thread.student_name}</span>
                      </div>
                      <p className="text-sm text-slate-500 truncate mt-1">
                        {thread.last_message || "No messages yet"}
                      </p>
                    </div>
                    {thread.parent_unread_count > 0 && (
                      <Badge className="bg-indigo-600 text-white text-xs">
                        {thread.parent_unread_count}
                      </Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </ScrollArea>
        </Card>

        {/* Chat Area */}
        <Card className="flex-1 flex flex-col">
          {selectedThread ? (
            <>
              <CardHeader className="py-3 px-4 border-b flex-row items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="md:hidden"
                    onClick={() => setSelectedThread(null)}
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </Button>
                  <Avatar className="w-10 h-10 bg-indigo-100">
                    <AvatarFallback className="bg-indigo-100 text-indigo-600">
                      {getInitials(selectedThread.teacher_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-slate-800">{selectedThread.teacher_name}</h3>
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <GraduationCap className="w-3 h-3" />
                      About {selectedThread.student_name}
                    </p>
                  </div>
                </div>
              </CardHeader>

              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_type === 'parent' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${
                        msg.sender_type === 'parent' 
                          ? 'bg-indigo-600 text-white rounded-l-lg rounded-tr-lg' 
                          : 'bg-slate-100 text-slate-800 rounded-r-lg rounded-tl-lg'
                      } p-3`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <div className={`flex items-center gap-1 mt-1 text-xs ${
                          msg.sender_type === 'parent' ? 'text-indigo-200' : 'text-slate-400'
                        }`}>
                          <Clock className="w-3 h-3" />
                          {formatTime(msg.sent_at)}
                          {msg.sender_type === 'parent' && (
                            msg.read_at 
                              ? <CheckCheck className="w-3 h-3 ml-1" />
                              : <Check className="w-3 h-3 ml-1" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    className="resize-none min-h-[60px]"
                    rows={2}
                  />
                  <Button 
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || sendingMessage}
                    className="bg-indigo-600 hover:bg-indigo-700 self-end"
                  >
                    {sendingMessage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Select a conversation</p>
                <p className="text-sm">or start a new one</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* New Chat Modal */}
      <Dialog open={showNewChatModal} onOpenChange={setShowNewChatModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Start New Conversation</DialogTitle>
            <DialogDescription>
              Send a message to your child's teacher
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Select Teacher</label>
              <Select 
                value={selectedTeacher?.teacher_id || ""} 
                onValueChange={(value) => {
                  const teacher = teachers.find(t => t.teacher_id === value);
                  setSelectedTeacher(teacher);
                  setSelectedStudent(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a teacher" />
                </SelectTrigger>
                <SelectContent>
                  {teachers.map((teacher) => (
                    <SelectItem key={teacher.teacher_id} value={teacher.teacher_id}>
                      {teacher.teacher_name}
                      {teacher.subjects?.length > 0 && (
                        <span className="text-slate-400 ml-2">
                          ({teacher.subjects.join(", ")})
                        </span>
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTeacher && (
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-2">Regarding Student</label>
                <Select 
                  value={selectedStudent?.id || ""} 
                  onValueChange={(value) => {
                    const student = selectedTeacher.students.find(s => s.id === value);
                    setSelectedStudent(student);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select your child" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedTeacher.students.map((student) => (
                      <SelectItem key={student.id} value={student.id}>
                        {student.name} (Grade {student.grade})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">Message</label>
              <Textarea
                placeholder="Write your message here..."
                value={initialMessage}
                onChange={(e) => setInitialMessage(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewChatModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleStartNewChat}
              disabled={!selectedTeacher || !selectedStudent || !initialMessage.trim()}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
